import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Upload, X, Loader2, Link2, FileText, AlertTriangle } from 'lucide-react';
import { COUNTRIES, findCountry } from '../lib/countries';
import { FORM_LANGUAGES, TRANSLATIONS } from '../lib/onboardingTranslations';

const API = process.env.REACT_APP_BACKEND_URL;

const APPT_OPTIONS = ['Zoom', 'Google Meet', 'In-Person', 'Phone Call', 'Other'];
const DOMAIN_OPTIONS = ['I have access', 'Other'];
const BIZ_TYPES = ['Sole Proprietorship', 'Limited Liability Company (LLC)', 'C Corporation (C Corp)', 'S Corporation (S Corp)'];
const BIZ_TYPES_INTL = ['Sole Proprietorship / Individual', 'Partnership', 'Private Limited Company (Ltd)', 'Corporation', 'Non-Profit / NGO', 'Other'];

// Module-level so its identity is stable across renders — defining this inside
// the page component remounted the input on every keystroke and dropped focus.
const Field = ({ k, label, help, placeholder, type = 'text', textarea, icon, ctx }) => {
  const { answers, set, errors, labelCls, helpCls, inputCls, sub } = ctx;
  return (
    <div className="mb-6">
      <label className={labelCls}>{label} <span className="text-red-400">*</span></label>
      {help && <p className={helpCls}>{help}</p>}
      <div className="relative">
        {icon && <Link2 size={16} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${sub}`} />}
        {textarea ? (
          <textarea rows={4} value={answers[k] || ''} onChange={e => set(k, e.target.value)} placeholder={placeholder} className={inputCls} data-testid={`ob-field-${k}`} />
        ) : (
          <input type={type} value={answers[k] || ''} onChange={e => set(k, e.target.value)} placeholder={placeholder} className={`${inputCls} ${icon ? 'pl-10' : ''}`} data-testid={`ob-field-${k}`} />
        )}
      </div>
      {errors[k] ? <p className="text-red-400 text-xs mt-1.5">{errors[k]}</p> : null}
    </div>
  );
};

export default function PublicOnboardingFormPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get('embed') === '1';
  const preview = searchParams.get('preview') === '1';

  const [form, setForm] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(searchParams.get('theme') || '');
  const [phoneCountry, setPhoneCountry] = useState('');
  const [lang, setLang] = useState('en');
  const fileInputRef = useRef(null);
  const topRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => { formRef.current = form; }, [form]);

  const makeFakeDone = useCallback(() => {
    const f = formRef.current || {};
    return {
      submission_id: 'preview',
      first_name: 'Jane',
      thankyou_headline: f.thankyou_headline || 'Thank You {{first_name}}!',
      thankyou_subtext: f.thankyou_subtext || '',
      thankyou_cta_text: f.thankyou_cta_text || 'Continue',
      thankyou_cta_url: f.thankyou_cta_url || '#',
    };
  }, []);

  const applyPreviewStep = useCallback((n) => {
    const num = parseInt(n, 10) || 0;
    if (num >= 3) {
      setDone(makeFakeDone());
    } else {
      setDone(null);
      setStep(Math.max(0, Math.min(2, num)));
    }
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [makeFakeDone]);

  useEffect(() => {
    axios.get(`${API}/api/onboarding/public/${slug}`)
      .then(res => {
        setForm(res.data);
        if (preview) {
          const initial = searchParams.get('step');
          if (initial) setTimeout(() => applyPreviewStep(initial), 50);
          setTimeout(() => { try { window.parent?.postMessage({ type: 'ob_preview_ready' }, '*'); } catch { /* noop */ } }, 100);
        }
      })
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Preview mode: live-sync config/step/theme from the builder or preview shell
  useEffect(() => {
    if (!preview) return;
    const videoEmbedUrl = (url) => {
      const u = (url || '').trim();
      if (!u) return '';
      const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
      if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
      const loom = u.match(/loom\.com\/(?:share|embed)\/(\w+)/);
      if (loom) return `https://www.loom.com/embed/${loom[1]}`;
      const wistia = u.match(/(?:wistia\.com\/medias\/|wistia\.net\/embed\/iframe\/)(\w+)/);
      if (wistia) return `https://fast.wistia.net/embed/iframe/${wistia[1]}`;
      return u;
    };
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === 'ob_preview_config' && d.config) {
        setForm(prev => ({
          ...(prev || {}),
          ...d.config,
          video_embed_url: videoEmbedUrl(d.config.video_url || ''),
          twilio_video_embed_url: videoEmbedUrl(d.config.twilio_video_url || ''),
        }));
      } else if (d.type === 'ob_preview_step') {
        applyPreviewStep(d.step);
      } else if (d.type === 'ob_preview_theme') {
        setPreviewTheme(d.theme || '');
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [preview, applyPreviewStep]);

  useEffect(() => {
    if (!embedded || preview) return;
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    document.documentElement.classList.add('ob-embed');
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
      document.documentElement.classList.remove('ob-embed');
    };
  }, [embedded, preview]);

  // Auto-height: tell the host page how tall the form is so the iframe can
  // grow to fit — no internal scrollbox, the host page scrolls naturally.
  useEffect(() => {
    if (!embedded || preview) return;
    const post = () => {
      const zoom = parseFloat(getComputedStyle(document.documentElement).zoom || '1') || 1;
      const h = Math.ceil(document.body.scrollHeight * zoom) + 8;
      try { window.parent?.postMessage({ type: 'ob_embed_height', height: h }, '*'); } catch { /* noop */ }
    };
    post();
    const t = setTimeout(post, 600);
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [embedded, preview]);

  const set = (key, val) => {
    setAnswers(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  // US region: country auto-fills to United States
  useEffect(() => {
    if (!form) return;
    if ((form.region || 'us') === 'us') {
      setAnswers(prev => (prev.business_country ? prev : { ...prev, business_country: 'United States' }));
    }
  }, [form]);

  const dark = ((preview && previewTheme) ? previewTheme : (form?.theme || 'dark')) === 'dark';
  const colors = form?.outline_type === 'solid'
    ? [form?.outline_colors?.[0] || '#38bdf8']
    : (form?.outline_colors?.length ? form.outline_colors : ['#38bdf8', '#a855f7', '#f43f5e']);
  const gradient = colors.length > 1
    ? `linear-gradient(135deg, ${colors.join(', ')})`
    : colors[0];
  const glow = form?.glow_color || '#2dd4bf';

  const pageBg = dark ? '#050608' : '#f1f3f7';
  // Real site embeds get a transparent background so the host page's own
  // colors/branding show through. Hosted link + preview keep black/white.
  const transparentBg = embedded && !preview;
  const cardBg = dark ? '#14161d' : '#ffffff';
  const text = dark ? 'text-white' : 'text-gray-900';
  const sub = dark ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-4 py-3 rounded-lg border focus:outline-none transition-colors ${
    dark ? 'bg-[#1e212b] border-white/10 text-white focus:border-sky-400/60' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-sky-500'
  }`;
  const labelCls = `block font-semibold mb-2 ${text}`;
  const helpCls = `text-sm mb-3 -mt-1 ${sub}`;

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const uploadFile = useCallback(async (file) => {
    if (!file || preview) return;
    if (file.size > 20 * 1024 * 1024) { setErrors(p => ({ ...p, docs: t.maxFile })); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/onboarding/public/${slug}/upload`, fd);
      setDocs(prev => [...prev, res.data]);
    } catch {
      setErrors(p => ({ ...p, docs: t.uploadFailed }));
    } finally {
      setUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, t]);

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      ['first_name', 'last_name', 'business_legal_name', 'business_address', 'business_country', 'business_phone', 'business_email', 'website', 'team_leads'].forEach(k => {
        if (!(answers[k] || '').trim()) e[k] = t.required;
      });
      if ((answers.business_email || '').trim() && !/^\S+@\S+\.\S+$/.test(answers.business_email.trim())) e.business_email = t.validEmail;
    }
    if (step === 1) {
      ['qualifying_questions', 'faqs_objections', 'comm_channels'].forEach(k => {
        if (!(answers[k] || '').trim()) e[k] = t.required;
      });
      if (!answers.appointment_location) e.appointment_location = t.selectOne;
      if (!answers.domain_access) e.domain_access = t.selectOne;
      if (!answers.has_team_emails) e.has_team_emails = t.selectOne;
      if (answers.has_team_emails === 'Yes' && !(answers.team_emails || '').trim()) e.team_emails = t.required;
      if (!answers.has_scripts) e.has_scripts = t.selectOne;
    }
    if (step === 2) {
      if (!answers.twilio_status) e.twilio_status = t.selectOne;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (preview || validateStep()) { setStep(s => Math.min(2, s + 1)); scrollTop(); } };

  const submit = async () => {
    if (preview) {
      setDone({ ...makeFakeDone(), first_name: (answers.first_name || '').trim() || 'Jane' });
      scrollTop();
      return;
    }
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const pc = findCountry(phoneCountry || form?.default_phone_country || 'US');
      const num = (answers.business_phone || '').trim();
      const submitAnswers = {
        ...answers,
        business_phone: num.startsWith('+') ? num : `${pc.dial} ${num}`.trim(),
        business_phone_country: pc.code,
        form_language: lang,
        email_me_copy: answers.email_me_copy === 'yes' ? 'yes' : 'no',
      };
      const res = await axios.post(`${API}/api/onboarding/public/${slug}/submit`, { answers: submitAnswers, docs });
      setDone(res.data);
      scrollTop();
    } catch (err) {
      setErrors(p => ({ ...p, _submit: err.response?.data?.detail || t.submitFailed }));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#050608' }}>
      <p className="text-gray-400">This onboarding form is not available.</p>
    </div>
  );
  if (!form) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: (embedded && !preview) ? 'transparent' : pageBg }}>
      <Loader2 className="animate-spin text-sky-400" size={32} />
    </div>
  );

  const Radio = ({ field, options, columns = 2 }) => (
    <div className={`grid gap-2.5 ${columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => set(field, opt)}
          className={`text-left px-4 py-3.5 rounded-lg border transition-all flex items-center gap-3 ${
            answers[field] === opt
              ? 'border-sky-400 bg-sky-400/10'
              : dark ? 'border-white/10 bg-[#1e212b] hover:border-white/25' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
          }`}
          data-testid={`ob-radio-${field}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${answers[field] === opt ? 'border-sky-400' : dark ? 'border-gray-500' : 'border-gray-400'}`}>
            {answers[field] === opt && <span className="w-2 h-2 rounded-full bg-sky-400" />}
          </span>
          <span className={`text-sm ${text}`}>{t.opt[opt] || opt}</span>
        </button>
      ))}
    </div>
  );

  const Err = ({ k }) => errors[k] ? <p className="text-red-400 text-xs mt-1.5">{errors[k]}</p> : null;

  const fieldCtx = { answers, set, errors, labelCls, helpCls, inputCls, sub };

  const GradientBtn = ({ children, onClick, disabled, testId }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-8 py-3 rounded-xl font-semibold text-white transition-transform hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100"
      style={{ background: gradient, boxShadow: `0 0 24px ${glow}55` }}
      data-testid={testId}
    >
      {children}
    </button>
  );

  const platform = form.comm_platform || 'Pumble';
  const isUS = (form.region || 'us') === 'us';
  const effPhoneCountry = findCountry(phoneCountry || form.default_phone_country || 'US');

  return (
    <div className={transparentBg ? '' : 'min-h-screen py-0'} style={{ background: transparentBg ? 'transparent' : pageBg }}>
      <div ref={topRef} />
      {/* Language selector */}
      <div className="max-w-3xl mx-auto px-4 pt-3 flex justify-end">
        <select
          value={lang}
          onChange={e => setLang(e.target.value)}
          className={`text-sm px-3 py-1.5 rounded-lg border cursor-pointer focus:outline-none transition-colors ${
            dark ? 'bg-[#1e212b] border-white/10 text-white hover:border-white/25' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
          }`}
          data-testid="ob-language-select"
        >
          {FORM_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
        </select>
      </div>
      {/* Stepper */}
      <div
        className={transparentBg ? 'py-4' : 'sticky top-0 z-20 py-4 border-b'}
        style={transparentBg ? {} : { background: pageBg, borderColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)' }}
      >
        <div className="flex items-center justify-center gap-3 sm:gap-6 px-4">
          {t.steps.map((label, i) => {
            const complete = done ? true : step > i;
            const active = !done && step === i;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-2.5">
                  <span
                    className="w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all"
                    style={{
                      borderColor: complete || active ? '#0284c7' : dark ? '#2a2e3a' : '#cbd5e1',
                      background: complete ? '#0369a1' : active ? 'rgba(2,132,199,0.15)' : 'transparent',
                    }}
                  >
                    {complete ? <Check size={16} className="text-white" /> : <span className={`w-2.5 h-2.5 rounded-full ${active ? 'bg-sky-400' : dark ? 'bg-gray-600' : 'bg-gray-400'}`} />}
                  </span>
                  <span className={`text-sm sm:text-base font-medium ${active ? 'text-sky-400' : complete ? text : sub}`}>{label}</span>
                </div>
                {i < t.steps.length - 1 && <span className="hidden sm:block w-16 h-px" style={{ background: dark ? '#2a2e3a' : '#cbd5e1' }} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className={`max-w-3xl mx-auto px-4 ${embedded ? 'py-6' : 'py-10'}`}>
        {/* Gradient-outlined card */}
        <div className="rounded-2xl p-[2px]" style={{ background: gradient, boxShadow: `0 0 60px ${glow}30` }}>
          <div className="rounded-2xl px-6 sm:px-10 py-10" style={{ background: cardBg }} data-testid="onboarding-form-card">

            {done ? (
              /* ─── Thank You ─── */
              <div className="text-center py-6" data-testid="ob-thankyou">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(56,189,248,0.15)' }}>
                  <Check size={30} className="text-sky-400" />
                </div>
                <h1 className={`text-2xl sm:text-3xl font-bold mb-4 ${text}`}>
                  {(done.thankyou_headline || 'Thank you {{first_name}}!').replace(/\{\{\s*first_name\s*\}\}/g, done.first_name || '')}
                </h1>
                {done.thankyou_subtext && (
                  <p className={`${sub} whitespace-pre-line mb-8`}>{done.thankyou_subtext}</p>
                )}
                {done.thankyou_cta_url && (
                  <a
                    href={done.thankyou_cta_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-full py-4 rounded-lg font-bold text-white text-center text-sm sm:text-base"
                    style={{ background: '#0ea5e9' }}
                    data-testid="ob-thankyou-cta"
                  >
                    {done.thankyou_cta_text || 'Continue'}
                  </a>
                )}
              </div>
            ) : (
              <>
                {step === 0 && (
                  <>
                    <h1 className={`text-2xl sm:text-3xl font-bold text-center mb-2 ${text}`}>{form.form_title}</h1>
                    <p className={`text-center mb-8 ${sub}`}>{form.form_subtitle}</p>

                    {form.video_embed_url && (
                      <>
                        <div className="flex items-center justify-center gap-2 px-4 py-3.5 mb-5 rounded-xl border" style={{ borderColor: '#eab308' }}>
                          <AlertTriangle size={16} className="text-yellow-500 shrink-0" />
                          <p className="text-yellow-500 font-semibold text-sm sm:text-base text-center">{form.video_cta}</p>
                        </div>
                        <div className="mb-8 rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                          <iframe
                            src={form.video_embed_url}
                            title="Walkthrough video"
                            className="w-full h-full"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                          />
                        </div>
                      </>
                    )}

                    <Field ctx={fieldCtx} k="first_name" label={t.firstName} />
                    <Field ctx={fieldCtx} k="last_name" label={t.lastName} />
                    <Field ctx={fieldCtx} k="business_legal_name" label={t.legalName} />
                    <Field ctx={fieldCtx} k="business_address" label={t.address} placeholder={isUS ? t.addressPhUS : t.addressPhIntl} />
                    <div className="mb-6 -mt-2">
                      <label className={labelCls}>{t.country} <span className="text-red-400">*</span></label>
                      <select
                        value={answers.business_country || ''}
                        onChange={e => set('business_country', e.target.value)}
                        className={inputCls}
                        data-testid="ob-field-business_country"
                      >
                        <option value="">{t.selectCountry}</option>
                        {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
                      </select>
                      <Err k="business_country" />
                    </div>
                    <div className="mb-6">
                      <label className={labelCls}>{t.phoneLabel} <span className="text-red-400">*</span></label>
                      <div className="flex gap-2">
                        <select
                          value={effPhoneCountry.code}
                          onChange={e => setPhoneCountry(e.target.value)}
                          className={`${inputCls} shrink-0`}
                          style={{ width: 118 }}
                          data-testid="ob-field-phone-country"
                        >
                          {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.dial}</option>)}
                        </select>
                        <input type="tel" value={answers.business_phone || ''} onChange={e => set('business_phone', e.target.value)} placeholder={isUS ? t.phonePhUS : t.phonePhIntl} className={inputCls} data-testid="ob-field-business_phone" />
                      </div>
                      <Err k="business_phone" />
                    </div>
                    <Field ctx={fieldCtx} k="business_email" label={t.email} type="email" />
                    <Field ctx={fieldCtx} k="website" label={t.website} icon placeholder="https://" />
                    <Field ctx={fieldCtx} k="team_leads" label={t.teamLead} placeholder={t.teamLeadPh} />

                    <div className="mb-6">
                      <label className={labelCls}>{t.greetingLabel}</label>
                      <p className={helpCls}>{t.greetingHelp}</p>
                      <textarea rows={4} value={answers.greeting_intro || ''} onChange={e => set('greeting_intro', e.target.value)} className={inputCls} data-testid="ob-field-greeting_intro" />
                    </div>
                    <div className="mb-8">
                      <label className={labelCls}>{t.companyLabel}</label>
                      <p className={helpCls}>{t.companyHelp}</p>
                      <textarea rows={5} value={answers.company_details || ''} onChange={e => set('company_details', e.target.value)} className={inputCls} data-testid="ob-field-company_details" />
                    </div>

                    <div className="flex justify-center">
                      <GradientBtn onClick={next} testId="ob-continue-step1">{t.continueBtn}</GradientBtn>
                    </div>
                  </>
                )}

                {step === 1 && (
                  <>
                    <div className="mb-6">
                      <label className={labelCls}>{t.qualifyingLabel} <span className="text-red-400">*</span></label>
                      <p className={`${helpCls} whitespace-pre-line`}>{t.qualifyingHelp}</p>
                      <textarea rows={5} value={answers.qualifying_questions || ''} onChange={e => set('qualifying_questions', e.target.value)} className={inputCls} data-testid="ob-field-qualifying_questions" />
                      <Err k="qualifying_questions" />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{t.faqsLabel} <span className="text-red-400">*</span></label>
                      <p className={`${helpCls} whitespace-pre-line`}>{t.faqsHelp}</p>
                      <textarea rows={6} value={answers.faqs_objections || ''} onChange={e => set('faqs_objections', e.target.value)} className={inputCls} data-testid="ob-field-faqs_objections" />
                      <Err k="faqs_objections" />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{t.apptLabel} <span className="text-red-400">*</span></label>
                      <Radio field="appointment_location" options={APPT_OPTIONS} />
                      <Err k="appointment_location" />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{t.domainLabel} <span className="text-red-400">*</span></label>
                      <Radio field="domain_access" options={DOMAIN_OPTIONS} />
                      <Err k="domain_access" />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{t.commsLabel.replace('{platform}', platform)} <span className="text-red-400">*</span></label>
                      <input value={answers.comm_channels || ''} onChange={e => set('comm_channels', e.target.value)} placeholder={t.commsPh} className={inputCls} data-testid="ob-field-comm_channels" />
                      <Err k="comm_channels" />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{t.teamEmailsLabel} <span className="text-red-400">*</span></label>
                      <Radio field="has_team_emails" options={['Yes', 'No']} />
                      <Err k="has_team_emails" />
                      {answers.has_team_emails === 'Yes' && (
                        <div className={`mt-3 p-4 rounded-xl ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <label className={labelCls}>{t.inputEmails} <span className="text-red-400">*</span></label>
                          <textarea rows={3} value={answers.team_emails || ''} onChange={e => set('team_emails', e.target.value)} className={inputCls} data-testid="ob-field-team_emails" />
                          <Err k="team_emails" />
                        </div>
                      )}
                    </div>

                    <div className="mb-8">
                      <label className={labelCls}>{t.scriptsLabel} <span className="text-red-400">*</span></label>
                      <Radio field="has_scripts" options={['Yes', 'No']} columns={1} />
                      <Err k="has_scripts" />
                      {answers.has_scripts === 'Yes' && (
                        <div className={`mt-3 p-4 rounded-xl ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
                          <p className={`${text} font-medium mb-3`}>{t.scriptsIntro}</p>
                          <div
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => { e.preventDefault(); uploadFile(e.dataTransfer.files?.[0]); }}
                            className={`cursor-pointer border-2 border-dashed rounded-xl py-8 text-center transition-colors ${dark ? 'border-white/20 hover:border-sky-400/50' : 'border-gray-300 hover:border-sky-500'}`}
                            data-testid="ob-file-dropzone"
                          >
                            {uploading ? <Loader2 className="animate-spin mx-auto text-sky-400" size={24} /> : (
                              <>
                                <Upload size={24} className={`mx-auto mb-2 ${sub}`} />
                                <p className={`text-sm ${text}`}>{t.dragDrop} <span className="underline text-sky-400">{t.browse}</span></p>
                                <p className={`text-xs mt-1 ${sub}`}>{t.maxFile}</p>
                              </>
                            )}
                          </div>
                          <input ref={fileInputRef} type="file" className="hidden" onChange={e => { uploadFile(e.target.files?.[0]); e.target.value = ''; }} />
                          {docs.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {docs.map(d => (
                                <div key={d.doc_id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${dark ? 'bg-white/10 text-white' : 'bg-white text-gray-800 border border-gray-200'}`}>
                                  <FileText size={14} className="text-sky-400 shrink-0" />
                                  <span className="truncate flex-1">{d.filename}</span>
                                  <button onClick={() => setDocs(prev => prev.filter(x => x.doc_id !== d.doc_id))} className="text-gray-400 hover:text-red-400"><X size={14} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          <Err k="docs" />
                        </div>
                      )}
                    </div>

                    <div className="flex justify-center">
                      <GradientBtn onClick={next} testId="ob-continue-step2">{t.continueBtn}</GradientBtn>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <h2 className={`text-2xl font-bold mb-4 ${text}`}>{t.twilioTitle}</h2>
                    {form.twilio_video_embed_url && (
                      <>
                        {form.twilio_video_cta && (
                          <div className="flex items-center justify-center gap-2 px-4 py-3.5 mb-5 rounded-xl border" style={{ borderColor: '#eab308' }}>
                            <AlertTriangle size={16} className="text-yellow-500 shrink-0" />
                            <p className="text-yellow-500 font-semibold text-sm sm:text-base text-center">{form.twilio_video_cta}</p>
                          </div>
                        )}
                        <div className="mb-6 rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                          <iframe
                            src={form.twilio_video_embed_url}
                            title="Twilio walkthrough video"
                            className="w-full h-full"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                          />
                        </div>
                      </>
                    )}
                    <p className={`font-semibold mb-1 ${text}`}>{t.a2pTitle}</p>
                    <p className={`${sub} text-sm mb-5`}>
                      {isUS ? t.a2pUS : t.a2pIntl}
                    </p>
                    <ul className={`${sub} text-sm mb-6 space-y-1.5`}>
                      <li>• {t.websiteLink} <a href="https://www.twilio.com" target="_blank" rel="noreferrer" className="text-sky-400 underline">www.twilio.com</a></li>
                      {form.twilio_invite_email && (
                        <li>• {t.inviteEmail} <span className="text-amber-400 font-semibold italic">{form.twilio_invite_email}</span></li>
                      )}
                    </ul>

                    <div className="mb-6">
                      <label className={labelCls}>{t.twilioDone} <span className="text-red-400">*</span></label>
                      <Radio field="twilio_status" options={['I Have Finished Making My Twilio Account & Invited The Tech Team', 'I Need Help Creating My Twilio Account']} columns={1} />
                      <Err k="twilio_status" />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{t.bizType}</label>
                      <Radio field="business_type" options={isUS ? BIZ_TYPES : BIZ_TYPES_INTL} columns={1} />
                    </div>

                    <div className="mb-6">
                      <label className={labelCls}>{isUS ? t.ein : t.taxId}</label>
                      <input value={answers.business_ein || ''} onChange={e => set('business_ein', e.target.value)} className={inputCls} data-testid="ob-field-business_ein" />
                    </div>

                    <div className="mb-8">
                      <label className={labelCls}>{t.areaCodes}</label>
                      <textarea rows={3} value={answers.area_codes || ''} onChange={e => set('area_codes', e.target.value)} className={inputCls} data-testid="ob-field-area_codes" />
                    </div>

                    {/* Email me a copy — one webhook, checkbox state → send_client_copy yes/no */}
                    <div className={`border-t mb-6 ${dark ? 'border-white/10' : 'border-gray-200'}`} data-testid="ob-email-copy-divider" />
                    <label
                      className={`flex items-center gap-3 px-4 py-3.5 mb-6 rounded-lg border cursor-pointer transition-all ${
                        answers.email_me_copy === 'yes'
                          ? 'border-sky-400 bg-sky-400/10'
                          : dark ? 'border-white/10 bg-[#1e212b] hover:border-white/25' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      }`}
                      data-testid="ob-email-copy-row"
                    >
                      <input
                        type="checkbox"
                        checked={answers.email_me_copy === 'yes'}
                        onChange={e => set('email_me_copy', e.target.checked ? 'yes' : 'no')}
                        className="w-4 h-4 accent-sky-400 shrink-0 cursor-pointer"
                        data-testid="ob-email-copy-checkbox"
                      />
                      <span className={`text-sm ${text}`}>{t.emailCopyCheckbox} <span className={`${sub} italic`}>{t.optional}</span></span>
                    </label>

                    {errors._submit && <p className="text-red-400 text-sm mb-4 text-center">{errors._submit}</p>}

                    <div className="flex justify-center">
                      <GradientBtn onClick={submit} disabled={submitting} testId="ob-submit-btn">
                        {submitting ? t.submitting : t.submitBtn}
                      </GradientBtn>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
